import { useEffect, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useToast } from '../Context/ToastContext';
import { claimInvitation } from '../services/advisorNetworkService';

/**
 * Adviser-network invitations for people who are not on iMaven yet (2026-10-28).
 *
 * The invitation email links to /network?invite=<token> (or ?subcontract=<token> for a subcontractor invite). The
 * person usually has no account: they sign up, go through onboarding and land somewhere else, and the link is gone.
 * rememberInviteFromUrl() keeps the token from the first page load; once signed in, PendingInviteClaimer sends it to
 * POST /network/connections/claim, which attaches the invitation to the new account. It then waits in Network →
 * Invitations (or Subcontractors) for Accept / Decline.
 */
const KEY = 'imaven.pendingInvite';
const MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;

export function rememberInviteFromUrl() {
  try {
    const q = new URLSearchParams(window.location.search);
    const token = q.get('invite') || q.get('subcontract');
    if (token && /^[a-f0-9]{64}$/.test(token)) {
      window.localStorage.setItem(KEY, JSON.stringify({ token, kind: q.get('invite') ? 'network' : 'subcontractor', at: Date.now() }));
    }
  } catch { /* storage blocked: the email match on the server still finds the invitation */ }
}

function readPending() {
  try {
    const v = JSON.parse(window.localStorage.getItem(KEY) || 'null');
    if (!v?.token || Date.now() - (v.at || 0) > MAX_AGE_MS) { window.localStorage.removeItem(KEY); return null; }
    return v;
  } catch { return null; }
}

function forget() {
  try { window.localStorage.removeItem(KEY); } catch { /* ignore */ }
}

/** Rendered once inside the signed-in app (not for team-member logins). */
export default function PendingInviteClaimer() {
  const { isAuthenticated, getAccessTokenSilently } = useAuth0();
  const { success, warning } = useToast();
  const done = useRef(false);

  useEffect(() => {
    if (!isAuthenticated || done.current) return;
    const pending = readPending();
    if (!pending) return;
    done.current = true;
    (async () => {
      try {
        const token = await getAccessTokenSilently();
        const r = await claimInvitation(token, pending.token);
        forget();
        const where = r.relationshipType === 'subcontractor' ? 'Network → Subcontractors' : 'Network → Invitations';
        if (r.status === 'pending') success(`${r.inviterName || 'An adviser'} invited you to connect on iMaven. Open ${where} to accept.`);
      } catch (e) {
        const status = e?.response?.status;
        // A definite answer (gone, taken, already connected, your own invite): drop the token. A network error: try
        // again on the next load.
        if (status && status >= 400 && status < 500) {
          forget();
          const msg = e?.response?.data?.error;
          if (msg) warning(msg);
        } else {
          done.current = false;
        }
      }
    })();
  }, [isAuthenticated, getAccessTokenSilently, success, warning]);

  return null;
}
